import {BrowserModule} from '@angular/platform-browser';
import {Location} from '@angular/common';
import {Injector, NgModule, inject, provideAppInitializer} from '@angular/core';
import {AppComponent} from './app.component';
import {LoginComponent} from './login/login.component';
import {AppRoutingModule} from './app-routing.module';
import {HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi} from '@angular/common/http';
import {ReactiveFormsModule} from '@angular/forms';
import {environment} from '../environments/environment';
import {CustomAuthModule} from '@innobile/authmodule';
import {SharedModule} from './shared/shared.module';
import {CompanyUser} from './models/CompanyUser';
import {CompaniesInterceptor} from './shared/interceptors/companies.interceptor';
import {SharedVariablesService} from './services/shared-variables.service';
import {TableWebSocketAdapter} from '@innobile/tablemodule';
import {TableSocketAdapterService} from './services/table-socket-adapter.service';
import {provideTableDateSettings} from './shared/date-format/table-date-settings';
import {NotfoundInterceptor} from './shared/interceptors/notfound.interceptor';
import {PasswordResetComponent} from './password-reset/password-reset.component';
import {OAuthService, provideOAuthClient} from "angular-oauth2-oidc";
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {ISystemInfo, OAuthProvider} from './models/oauth';
import {AuthTokenService} from './services/auth-token.service';
import {SystemInfoInterceptor} from './shared/interceptors/system-info.interceptor';
import {AppInfoComponent} from './main/app-info/app-info.component';
import {rebaseSvgUrlReferences} from './shared/svg-url-references';

declare const require; // Use the require method provided by webpack
export let appInjector: Injector;

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    PasswordResetComponent
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    SharedModule,
    ReactiveFormsModule,
    CustomAuthModule.forRoot({
      config: {
        apiPrefix: environment.apiPrefix,
        userModel: CompanyUser,
        whitelistedApiCalls: ['authentication', 'users/reset', 'oauth2/login', 'system/info'],
        tokenRemover: () => appInjector.get(AuthTokenService).removeToken()
      }
    }),
    AppInfoComponent
  ],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: SystemInfoInterceptor,
      multi: true,
    },
    provideAppInitializer(() => {
      const initializerFn = downloadSystemInfo(inject(HttpClient), inject(SharedVariablesService));
      return initializerFn();
    }),
    // Safari's amCharts references go stale on every URL change — see `rebaseSvgUrlReferences`.
    provideAppInitializer(() => {
      inject(Location).onUrlChange(() => rebaseSvgUrlReferences());
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CompaniesInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NotfoundInterceptor,
      multi: true
    }, {
      provide: TableWebSocketAdapter,
      useClass: TableSocketAdapterService
    },
    provideTableDateSettings(),
    SharedVariablesService,
    provideOAuthClient({
      resourceServer: {
        sendAccessToken: true,
        customUrlValidation: (url: string) => url.includes('ws/oauth2/login')
      },
    }),
    AuthTokenService,
    OAuthService,
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {
  constructor(private injector: Injector) {
    appInjector = this.injector;
  }
}

export function downloadSystemInfo(http: HttpClient, sharedVars: SharedVariablesService) {
  return (): Observable<ISystemInfo> => {
      return http.get<ISystemInfo>('system/info').pipe(tap(res => {
        if (res.oauthInfo.enabled) {
          sharedVars.setVar('oauthProvider', new OAuthProvider(res));
        }
        sharedVars.setVar('weightUnit', res.weightUnit);
        sharedVars.setVar('apiVersion', { version: res.version, buildDate: res.buildDate });
      }));
  }
}

